import _, {difference, without} from 'lodash';
import {connect} from 'react-redux';

import {ConversationTeamStatusesEnum} from '../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {hasAssigneeFilter, hasTagFilter} from '../../../../srcCommon/inboxes/search/searchHelpers';
import {isDemoInbox} from '../../../helpers/modelHelpers';
import {
  findUnreadStatus,
  isConversationInTeamContext,
  UnreadStatusesEnum,
} from '../../../helpers/models/conversationHelpers';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';
import {InboxesSearchLocationsEnum} from '../../../models/url/models/inboxesUrlModel';
import {getViewerConversation} from '../../../services/legacy/legacySelectors';
import {getComments, getConversations} from '../../../store/data/dataSelectors';
import {getInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {updateSeenOnboardingTipsAction} from '../../../store/data/preferences/preferencesActions';
import {
  getCompletedStartGuideTasks,
  getSeenOnboardingTips,
} from '../../../store/data/preferences/preferencesSelectors';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../store/data/teams/teamsSelectors';
import {InboxesParametersTypesEnum} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {getCellsPanelPageConversationState} from '../../../store/panels/panelsSelectors';
import {getIsTeammateTourActive} from '../../../store/stateless/guidedDemoSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {InfoBubblesProvider} from './infoBubblesProvider';

/*
 * Constants.
 */

const TeammateTourSequence: ReadonlyArray<OnboardingTipTypesEnum> = [
  OnboardingTipTypesEnum.TEAMMATE_WORKSPACE,
  OnboardingTipTypesEnum.TEAM_WORKSPACE,
];

const TeammateTourConversationViewSequence: ReadonlyArray<OnboardingTipTypesEnum> = [
  OnboardingTipTypesEnum.P3,
  OnboardingTipTypesEnum.COMMENT_COMPOSER,
];

/*
 * Props.
 */

interface InfoBubblesProviderContainerPublicProps {
  panelId: AppPanelId;
}

/*
 * Selectors.
 */

const getActiveSequence = createSelector(
  [getIsTeammateTourActive, getSeenOnboardingTips, getUserTeams],
  (isTeammateTourActive, seenOnboardingTips, userTeams) => {
    if (!isTeammateTourActive) {
      return undefined;
    }

    const activeSequence = computeSequenceForTour(seenOnboardingTips, userTeams);

    // We're only interested in the unseen portion of the sequence.
    const unseenSequence = difference(activeSequence, seenOnboardingTips);
    return unseenSequence.length === 0 ? undefined : unseenSequence;
  },
);

const readBubbleMinimumUserCreationDate = 1583189862532;

const getShouldShowReadStatusBubble = createSelector(
  [getCellsPanelPageConversationState, getConversations, getInboxes, getSeenOnboardingTips, getUser],
  (cellsPanelState, conversations, inboxes, seenOnboardingTips, user) => {
    if (!seenOnboardingTips.includes(OnboardingTipTypesEnum.COMMENT_COMPOSER)) {
      return false;
    }

    if (user.createdAt < readBubbleMinimumUserCreationDate) {
      return false;
    }

    if (!cellsPanelState) {
      return false;
    }

    const {cellsContext, parameters} = cellsPanelState;

    if (parameters.type !== InboxesParametersTypesEnum.INBOX) {
      return false;
    }

    const inbox = inboxes[parameters.inboxId];
    if (!inbox || isDemoInbox(inbox)) {
      return false;
    }

    return _(cellsPanelState.conversationIds)
      .map((id) => conversations[id])
      .compact()
      .some((c) => {
        const isInTeamContext = isConversationInTeamContext(cellsContext, c);
        const unreadStatus = findUnreadStatus(cellsContext, c);

        return isInTeamContext && unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD;
      });
  },
);

/** User creation date set for April 26th, 2020 and later. */
const invitedFeatureTeamExperimentStart = 1587945660000;

/** Simple selector for private comment info bubble. Bulk of the viewing logic is in the
 * CommentViewer which checks if the comment is actually visible only to the user. */
const getShouldShowPrivateCommentBubble = createSelector(
  [getSeenOnboardingTips, getUser],
  (seenOnboardingTips, user) => {
    if (!seenOnboardingTips.includes(OnboardingTipTypesEnum.COMMENT_COMPOSER)) {
      return false;
    }

    if (user.createdAt < invitedFeatureTeamExperimentStart) {
      return false;
    }

    return true;
  },
);

/** Selector to decide to open FirstArchiveInfoBubble. Current logic is if a user is on a conversation and has commented once */
const getShouldShowFirstArchiveInfoBubble = createSelector(
  [getSeenOnboardingTips, getUser, getViewerConversation, getComments, getInboxes],
  (seenOnboardingTips, user, viewerConversation, comments, inboxes) => {
    // If we're not in the experiment, don't have a conversation, or have already seen the infoBubble no need to show.
    if (
      user.createdAt < invitedFeatureTeamExperimentStart ||
      !viewerConversation ||
      !seenOnboardingTips.includes(OnboardingTipTypesEnum.COMMENT_COMPOSER) ||
      seenOnboardingTips.includes(OnboardingTipTypesEnum.FIRST_CONVERSATION_ARCHIVE)
    ) {
      return false;
    }

    // If we're not in open or unassigned/assigned we don't need to archive
    if (viewerConversation.teamStatus !== ConversationTeamStatusesEnum.OPEN) {
      return false;
    }

    const {lastComment} = viewerConversation;
    if (!lastComment) {
      return false;
    }

    const currentComment = comments[lastComment.uid];

    // If we haven't made a comment no need to show archive infoBubble
    if (!currentComment || currentComment.authorId !== user.id) {
      return false;
    }

    const isPrivateConversation = _(viewerConversation.inboxIds)
      .map((inboxId) => inboxes[inboxId])
      .compact()
      .every((inbox) => isTeammateNamespace(inbox.namespace));
    const hasTrackers = _(viewerConversation.trackers).some((tracker) =>
      Boolean(tracker && tracker.teammateId !== user.id),
    );

    // If the conversation isn't private already, we won't show the private inbox comment infoBubble first
    if (
      seenOnboardingTips.includes(OnboardingTipTypesEnum.PRIVATE_INBOX_COMMENT) ||
      !isPrivateConversation ||
      hasTrackers
    ) {
      return true;
    }

    // If we haven't seen the private inbox comment info bubble, we need to check that we have @mentioned users
    return Boolean(currentComment.mentions?.length);
  },
);

const getShouldShowLensTagSearchBubble = createSelector(
  [getCellsPanelPageConversationState],
  (cellsPanelState) => {
    if (!cellsPanelState) {
      return false;
    }

    const {parameters} = cellsPanelState;
    const {type, search} = parameters;

    // Don't show if a user is searching in only their personal inbox.
    if (type === InboxesParametersTypesEnum.USER) {
      const location = search?.location;
      if (!location || location === InboxesSearchLocationsEnum.LOCAL) {
        return false;
      }
    }

    const query = search?.query;

    if (!query) {
      return false;
    }

    return hasTagFilter(query);
  },
);

const getShouldShowLensAssigneeSearchBubble = createSelector(
  [getCellsPanelPageConversationState],
  (cellsPanelState) => {
    if (!cellsPanelState) {
      return false;
    }

    const {parameters} = cellsPanelState;
    const {type, search} = parameters;

    // Don't show if a user is searching in only their personal inbox.
    if (type === InboxesParametersTypesEnum.USER) {
      const location = search?.location;
      if (!location || location === InboxesSearchLocationsEnum.LOCAL) {
        return false;
      }
    }

    const query = search?.query;

    if (!query) {
      return false;
    }

    return hasAssigneeFilter(query);
  },
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InfoBubblesProviderContainerPublicProps>()(() => ({
  seenOnboardingTips: getSeenOnboardingTips,
  activeSequence: getActiveSequence,
  isTeammateTourActive: getIsTeammateTourActive,
  completedStartGuideTasks: getCompletedStartGuideTasks,
  shouldShowReadStatusBubble: getShouldShowReadStatusBubble,
  shouldShowPrivateCommentBubble: getShouldShowPrivateCommentBubble,
  shouldShowFirstArchiveInfoBubble: getShouldShowFirstArchiveInfoBubble,
  shouldShowLensTagSearchBubble: getShouldShowLensTagSearchBubble,
  shouldShowLensAssigneeSearchBubble: getShouldShowLensAssigneeSearchBubble,
}));

const dispatchProps = {
  updateSeenOnboardingTips: updateSeenOnboardingTipsAction,
  navigateToLinkAction,
};

export const InfoBubblesProviderContainer = connect(mapStateToProps, dispatchProps)(InfoBubblesProvider);

/*
 * Helpers.
 */

function computeSequenceForTour(
  seenOnboardingTips: ReadonlyArray<OnboardingTipTypesEnum>,
  userTeams: ReadonlyArray<TeamModel>,
) {
  // If we've already seen the first half of the teammate tour, move on to the conversation view tour if available.
  if (seenOnboardingTips.includes(OnboardingTipTypesEnum.P2)) {
    return TeammateTourConversationViewSequence.filter((i) => i !== OnboardingTipTypesEnum.P3);
  }

  // Otherwise the teammate tour is active. We remove the team workspace bubble if the user has no teams.
  return userTeams.length > 0
    ? TeammateTourSequence
    : without(TeammateTourSequence, OnboardingTipTypesEnum.TEAM_WORKSPACE);
}
